import intl from 'react-intl-universal';
import { useRef, useEffect, useState, useCallback } from 'react';
import { Drawer, Switch, Space, message } from 'antd';
import { default as Button } from '@/components/CssBtn';

import { ProColumnType, ProTable } from '@ant-design/pro-components';
import { DragSortTable } from '@ant-design/pro-table';
import { FormItemValueType } from '../../dynamicForm';
import { setDiyList } from '@/api/common';
import type { DiyFieldItem, DynamicTableType } from '@/type/dynamicTable';

import style from '../index.less';
import { isArray, slice, take } from 'lodash';

interface Props {
    // isShowAllField: boolean;
    tableType: DynamicTableType;
    show: boolean;
    columns: ProColumnType<any, FormItemValueType>[];
    showFields: DiyFieldItem[];
    onClose: () => void;
    onSuccess: (result: DiyFieldItem[]) => void;
}

const TableSettings: React.FC<Props> = ({
    // isShowAllField,
    tableType: type,
    columns: originDataSource = [],
    show,
    showFields,
    onSuccess,
    onClose,
}) => {
    const [noSortData, setNoSortData] = useState<DiyFieldItem[]>([]);
    const [dataSource, setDataSource] = useState<DiyFieldItem[]>([]);
    const dataSourceRef = useRef<DiyFieldItem[]>([]);

    useEffect(() => {
        if (showFields?.length) {
            const columns = originDataSource.map((col) => {
                const field = showFields.find((field) => {
                    const dataIndex = isArray(col.dataIndex)
                        ? col.dataIndex.join('.')
                        : col.dataIndex;
                    return dataIndex === field.field;
                });

                return {
                    ...field,
                    status: field ? field?.status : true,
                    title: col?.title || '',
                    disabled: col?.disabled,
                };
            });

            const noSortData = columns.filter((i) => i.disabled);

            if (noSortData.length) {
                const sortData = columns.filter((i) => !i.disabled);
                setDataSource(sortData);
                setNoSortData(noSortData);
                dataSourceRef.current = sortData;
            } else {
                // 如果没事设置不可移除项，取前 5 个字段不可配置
                const sortData = slice(columns, 5);
                setDataSource(sortData);
                setNoSortData(take(columns, 5));
                dataSourceRef.current = sortData;
            }
        } else {
            resetSetting();
        }
    }, [showFields]);

    const onSave = () => {
        const showFields = (noSortData || [])
            .map((item) => ({
                field: item.field,
                status: item.status,
            }))
            .concat(
                dataSource.map((item) => ({
                    field: item.field,
                    status: item.status,
                })) || [],
            );
        setDiyList({
            type,
            showFields,
        }).then(() => {
            message.success(intl.get('保存成功'));
            onSuccess?.(noSortData.concat(dataSource));
        });
    };

    const onColChange = (
        item: ProColumnType<any, FormItemValueType>,
        index: number,
    ) => {
        dataSourceRef.current.splice(index, 1, {
            ...item,
            status: !item.status,
        });

        setDataSource([...dataSourceRef.current]);
    };

    const tableColumns = useCallback(
        (disabled) => {
            return [
                {
                    title: intl.get('排序'),
                    dataIndex: 'sort',
                    width: 30,
                    // className: 'drag-visible',
                },
                {
                    dataIndex: 'title',
                },
                {
                    dataIndex: 'action',
                    width: 30,
                    render: (text, col, index) => {
                        return (
                            <Switch
                                disabled={!!col.disabled || disabled}
                                key={col.dataIndex}
                                size="small"
                                checked={col.status}
                                onChange={() => {
                                    onColChange(col, index);
                                }}
                            />
                        );
                    },
                },
            ];
        },
        [dataSource],
    );

    const handleDragSortEnd = (newDataSource: any) => {
        setDataSource(newDataSource);
        dataSourceRef.current = newDataSource;
    };

    const resetSetting = () => {
        const columns = originDataSource.map((col) => {
            return {
                field: isArray(col.dataIndex)
                    ? col.dataIndex.join('.')
                    : col.dataIndex,
                status: true,
                title: col.title,
                disabled: col?.disabled,
            };
        });

        const noSortData = columns.filter((i) => i.disabled);

        if (noSortData.length) {
            const sortData = columns.filter((i) => !i.disabled);
            setDataSource(sortData);
            setNoSortData(noSortData);
            dataSourceRef.current = sortData;
        } else {
            // 如果没事设置不可移除项，取前 5 个字段不可配置
            const sortData = slice(columns, 5);
            console.log('---', sortData);
            setDataSource(sortData);
            setNoSortData(take(columns, 5));
            dataSourceRef.current = sortData;
        }

        // const sortData = columns.filter((i) => !i.disabled);
        // setDataSource(sortData);
        // setNoSortData(columns.filter((i) => i.disabled));
        // dataSourceRef.current = sortData;
    };

    return (
        <Drawer
            title={intl.get('表格列设置')}
            placement="right"
            open={show}
            onClose={onClose}
            extra={
                <Space>
                    <Button
                        size="middle"
                        onClick={resetSetting}
                    >
                        {intl.get('重置')}
                    </Button>
                    <Button
                        size="middle"
                        onClick={onSave}
                        type="primary"
                    >
                        {intl.get('保存')}
                    </Button>
                </Space>
            }
        >
            <ProTable
                className={style.drawerTable}
                dataSource={noSortData}
                columns={tableColumns(true)}
                rowKey="field"
                pagination={false}
                search={false}
                options={false}
                showHeader={false}
                size="small"
            ></ProTable>
            <DragSortTable
                className={style.drawerTable}
                dataSource={dataSource}
                columns={tableColumns(false)}
                rowKey="field"
                pagination={false}
                search={false}
                dragSortKey="sort"
                onDragSortEnd={handleDragSortEnd}
                options={false}
                showHeader={false}
                size="small"
                scroll={{
                    x: 'auto',
                    y: 500,
                }}
            ></DragSortTable>
        </Drawer>
    );
};

export default TableSettings;
